// src/components/TrialsPage/TrialDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useContactModal } from '../../context/ContactModalContext';
import { fixEncodingIssues, formatTreatment } from '../../utils';
import { useTranslation } from 'react-i18next';
import { useTrials } from '../../context/TrialsContext';
import { Trial } from '../../types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const TrialDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { nctId } = useParams<{ nctId: string }>();
  const locationData = useLocation();
  const navigate = useNavigate();
  const { openModal } = useContactModal();
  const { trials } = useTrials();

  const [trial, setTrial] = useState<Trial | null>(null);

  // ✅ Buscar el trial en contexto, o en sessionStorage si no existe (refresh / acceso directo)
  useEffect(() => {
    const fetchTrialById = async () => {
      if (!nctId) return;

      // 1. Buscar en contexto
      const foundInContext = trials.find((t) => t.nctId === nctId);
      if (foundInContext) {
        setTrial(foundInContext);
        return;
      }

      // 2. Buscar en sessionStorage
      const stored = sessionStorage.getItem('trials');
      if (stored) {
        try {
          const parsed: Trial[] = JSON.parse(stored);
          const foundInStorage = parsed.find((t) => t.nctId === nctId);
          if (foundInStorage) {
            setTrial(foundInStorage);
            return;
          }
        } catch (err) {
          console.error('Error parsing trials from sessionStorage', err);
        }
      }

      // 3. Como último recurso, hacer fetch del trial individual
      try {
        // const response = await fetch(`${import.meta.env.VITE_LOCAL_BACKEND}get-data?nctId=${nctId}`);
        const response = await fetch(`get-data?nctId=${nctId}`);
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const data: Trial[] = await response.json();

        if (data.length > 0) {
          setTrial(data[0]);
        }
      } catch (err) {
        console.error('Error fetching trial from API:', err);
      }
    };

    fetchTrialById();
  }, [nctId, trials]);

  if (!trial) {
    return (
      <Box p={4}>
        <Typography variant="h5" gutterBottom>
          {t('trialNotFound')}
        </Typography>
        <Button variant="contained" onClick={() => navigate(`/trials${locationData.search}`)}>
          {t('backButton')}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#FAFAFA',
          p: 2,
          borderRadius: 1,
          mb: 3,
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mr: { md: 2 },
              mb: 0,
            }}
          >
            {trial.briefTitle}
          </Typography>

          <Button
            variant="contained"
            sx={{
              backgroundColor: '#FF5722',
              '&:hover': { backgroundColor: '#E64A19' },
              textTransform: 'none',
              alignSelf: { xs: 'stretch', md: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
            }}
            onClick={() => openModal(trial.briefTitle, trial.nctId, trial.otherStudyId)}
          >
            {t('contactButton')}
          </Button>
        </Box>
      </Box>

      {/* Trial Details */}
      <Typography variant="body1" gutterBottom>
        <strong>{t('nct')}:</strong> {trial.nctId}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('otherId')}:</strong> {fixEncodingIssues(trial.otherStudyId)}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('leadSponsor')}:</strong> {trial.leadSponsor}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('organSystem')}:</strong>{' '}
        {Array.isArray(trial.organSystem) ? trial.organSystem.join(', ') : trial.organSystem}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('therapyLine')}:</strong> {trial.therapyLine}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('cancerStage')}:</strong> {trial.cancerStage}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('biomarker')}:</strong> {trial.biomarker}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('conditions')}:</strong> {trial.conditions.join(', ')}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('treatment')}:</strong> {formatTreatment(trial.treatment)}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('officialTitle')}:</strong> {trial.officialTitle}
      </Typography>
      <Typography variant="body1" gutterBottom component="div">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{trial.briefSummary}</ReactMarkdown>
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('locations')}:</strong> {trial.locations.join(', ')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: -1, mb: 2 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
        <Typography variant="caption">
          {t('locationNotice') || 'Not seeing your city? You can still request info about any trial.'}
        </Typography>
      </Box>
      <Typography variant="body1" gutterBottom component="div">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{trial.eligibilityModule.eligibilityCriteria}</ReactMarkdown>
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{t('moreInformation')}:</strong>{' '}
        <a href={trial.url} target="_blank" rel="noopener noreferrer">
          {trial.url}
        </a>
      </Typography>

      <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate(-1)}>
        {t('backButton')}
      </Button>
    </Box>
  );
};

export default TrialDetailPage;
